import { ExpandableFilterProps, ExpandableGroup, SortGroupName } from '../../components/expandable-filter/types'
import React from 'react'
import {
    IconAcceleration,
    IconBikeframe,
    IconBodyStyle,
    IconBodyStyleBike,
    IconBodyStyleVan,
    IconBootSpace,
    IconBrand,
    IconBuyOnline,
    IconCabType,
    IconCalendar,
    IconChannelCar,
    IconColour,
    IconDistance,
    IconDoors,
    IconDrivetrain,
    IconEbike,
    IconElectric,
    IconEmissions,
    IconEnginePower,
    IconEngineSize,
    IconFuelConsumption,
    IconFuelType,
    IconInsurance,
    IconManualGearbox,
    IconRange,
    IconRoadTax,
    IconSearch,
    IconSeats,
    IconSellerType,
    IconSort,
    IconSpeedometer,
    IconTag,
    IconVAT,
    IconWheel,
    IconWrittenOff,
} from '@at/design-system-icons'
import { FacetGroupName, FacetGroupStatus, FilterName } from '../../__generated__/fed-graphql-types'
import { SortOrderRadioButtons } from '../../components/sort-by/sort-order-radio-buttons'
import { FacetGroupNamesFallback } from '../../components/expandable-filter/expandandle-filter-titles'
import { SearchFiltersPostCodeInput as Postcode } from '../../components/search-filters-postcode-input/search-filters-postcode-input'
import { Distance } from '../filters/distance'
import { Make } from '../filters/make'
import { Model } from '../filters/model'
import { ModelVariant } from '../filters/model-variant'
import { ValuesChangedTuple } from '../hocs/types'
import { DigitalRetailingCheckbox } from '../filters/digital-retailing'
import { getFinanceDefaults, NewPriceToggle, Price } from '../filters/price'
import { FieldsetPanel } from '../../components/fieldset-panel/fieldset-panel'
import { ENewFilterRangeDesktop } from '../filters/static-props'
import { Channel } from '@at/sauron-platform-core'
import {
    FlexibleFinanceDeposit,
    FlexibleFinanceTerm,
    FlexibleFinanceYearlyMileage,
    MonthlyPriceExpander,
    FlexibleMonthlyPriceMax,
    FlexibleMonthlyPriceMin,
    FinanceDeposit,
    FinanceTerm,
    FinanceYearlyMileage,
    MonthlyPriceMax,
    MonthlyPriceMin,
} from '../filters/finance'
import { FinanceDisclaimerText } from '../other/finance-disclaimer-text/finance-disclaimer-text'
import { YearMinAndMax } from '../filters/year'
import { NewTransmission } from '../filters/transmission'
import { BodyTypeCars } from '../filters/body-type'
import { ColourCheckboxes } from '../filters/colour'
import { Doors } from '../filters/doors'
import { Seats, SeatsMaxFacetAndFilter, SeatsMinFacetAndFilter } from '../filters/seats'
import { FuelType } from '../filters/fuel-type-multiselect'
import { BatteryRange } from '../filters/battery-range'
import { InfoBanner } from '../../components/info-banner/info-banner'
import { BatteryQuickChargeTime } from '../filters/battery-quick-charge-time'
import { BatteryChargeTime } from '../filters/battery-charge-time'
import { EngineSizeCCMax, EngineSizeCCMin, EngineSizeMax, EngineSizeMin } from '../filters/engine-size'
import { EnginePowerMax, EnginePowerMin } from '../filters/engine-power'
import { Acceleration } from '../filters/acceleration'
import { FuelConsumption } from '../filters/fuel-consumption'
import { Co2Emissions } from '../filters/co2-emissions'
import { AnnualTax } from '../filters/annual-tax'
import { InsuranceGroup } from '../filters/insurance-group'
import { Drivetrain } from '../filters/drivetrain'
import { BootSpace } from '../filters/boot-space'
import { SellerType } from '../filters/seller-type'
import { WriteOff } from '../filters/write-off'
import { getKeywordsPlaceholder, Keywords } from '../filters/keywords/keywords'
import { SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facetGroups_FacetGroup } from '../../contexts/search-results-context/query/__generated__/search-results-facets.fed.query'
import { FrameSizeMaxFacetAndFilter, FrameSizeMinFacetAndFilter } from '../filters/frame_size_cm'
import { Category, CategoryNoLabel } from '../filters/category'
import { WheelBase } from '../filters/wheelbase'
import { CabType } from '../filters/cab-type'
import { VATStatus } from '../filters/vat-status'
import { Berth } from '../filters/berth'
import { BedroomLayout } from '../filters/bedroom-layout'
import { MTPLMMaxFacetAndFilter, MTPLMMinFacetAndFilter } from '../filters/mtplm'
import { LengthMaxFacetAndFilter, LengthMinFacetAndFilter } from '../filters/length'
import { DriverPosition } from '../filters/driver-position'
import { Axles } from '../filters/axles'
import { EndLayout } from '../filters/end-layout'
import { SubCategory } from '../filters/sub-category'
import { POA } from '../filters/poa'
import { AxleConfig } from '../filters/axle-config'
import { GrossVehicleWeightMax, GrossVehicleWeightMin } from '../filters/gross-vehicle-weight'
import { EmissionClass } from '../filters/emission-class'
import { GrossTrailerWeightMax, GrossTrailerWeightMin } from '../filters/gross-trailer-weight'
import { HoursUsedMax, HoursUsedMin } from '../filters/hours-used'
import { isTpf } from '@at/formatter-utils/lib/channel-utils/channel.util'
import { MileageKmMaxFacetAndFilter, MileageKmMinFacetAndFilter } from '../filters/mileage-km'
import {
    EditTerms,
    LeaseMaxMonthlyPrice,
    LeaseMaxMonthlyPriceExVat,
    LeaseMinMonthlyPrice,
    LeaseMinMonthlyPriceExVat,
    LeasingDeposit,
    LeasingPriceExpander,
    LeasingPriceExVatExpander,
    LeasingTerm,
    LeasingYearlyMileage,
    ResetToLowestMonthlyPrice,
} from '../filters/leasing'
import { ManufacturerApproved } from '../filters/manufacturer-approved'
import { NIOnly } from '../filters/ni-only'
import { LeaseInStockToggle } from '../filters/lease-in-stock'
import { LeaseProductTypeToggle } from '../filters/lease-product-type'
import { Location } from '../../components/search-filters-location/location'
import { MileageMinAndMax } from '../filters/mileage'
import { CategoryTag } from '../filters/category-tags'
import { Subtitle } from './expandable-filters.styles'
import { FlexibleFinanceFilterInfoBanner } from './flexible-finance-filter-info-banner'
import { DefaultTermsText } from '../../components/leasing-terms-toggle/leasing-terms-toggle'
import { Badge, BadgeCategories } from '@at/design-system-elements'

function getFacetGroup(
    name: FacetGroupName,
    queryFacetGroups:
        | readonly (SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facetGroups_FacetGroup | null)[]
        | null
): ExpandableGroup {
    const facetGroup = queryFacetGroups?.find((facetGroup) => facetGroup?.facetGroupName === name)
    const facetGroupNameWithPrice =
        facetGroup?.facetGroupName === FacetGroupName.monthly_price ? FacetGroupName.price : facetGroup?.facetGroupName
    return {
        status: facetGroup?.status || null,
        selectedValuesSummary: facetGroup?.selectedValuesSummary || null,
        title: facetGroup?.title || null,
        expandableGroupName: facetGroupNameWithPrice || name,
        helpText: facetGroup?.helpText || undefined,
        clearButtonLabel: facetGroup?.clearButtonLabel || null,
    }
}

export const expandableFilterDataMaker = (
    isSelected: (filter: FilterName) => boolean,
    queryFacetGroups:
        | readonly (SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facetGroups_FacetGroup | null)[]
        | null,
    sortByDisplayText: string,
    totalResults: number,
    hasSelected: (filter: FilterName, value: string) => boolean,
    channel: Channel,
    showFlexibleFinance: boolean
): Record<string, ExpandableFilterProps> => {
    const isChargeDisabled =
        getFacetGroup(FacetGroupName.charging_time, queryFacetGroups)?.status !== FacetGroupStatus.ENABLED
    const onMakeValueChanged = (values: string[]) => {
        return [
            values,
            [
                {
                    filter: FilterName.model,
                    selected: [],
                },
                {
                    filter: FilterName.aggregated_trim,
                    selected: [],
                },
            ],
        ] as ValuesChangedTuple
    }

    const onModelValueChanged = (values: string[]) => {
        return [
            values,
            [
                {
                    filter: FilterName.aggregated_trim,
                    selected: [],
                },
            ],
        ] as ValuesChangedTuple
    }
    const onStyleValueChanged = (values: string[]) => {
        return [
            values,
            [
                {
                    filter: FilterName.sub_style,
                    selected: [],
                },
            ],
        ] as ValuesChangedTuple
    }

    const enableMonthlyPrice = channel === Channel.cars || channel === Channel.vans || channel === Channel.bikes
    const enableModelVariant = channel === Channel.cars

    const bodyIconProps = {
        title: 'Body type filter',
        customSize: { height: 18, width: 18 },
    }

    const BodyTypeIcon: React.FC = () => {
        switch (channel) {
            case Channel.vans:
                return <IconBodyStyleVan {...bodyIconProps} />
            case Channel.bikes:
                return <IconBodyStyleBike {...bodyIconProps} />
            default:
                return <IconBodyStyle {...bodyIconProps} />
        }
    }

    function getPriceFilters() {
        if (hasSelected(FilterName.price_search_type, 'total')) {
            return {
                title: FacetGroupNamesFallback.price,
                group: getFacetGroup(FacetGroupName.price, queryFacetGroups),
                icon: <IconTag title="Price icon" />,
                children: (
                    <>
                        {enableMonthlyPrice ? (
                            <NewPriceToggle defaultFinanceValues={getFinanceDefaults(channel)} />
                        ) : null}
                        <FieldsetPanel>
                            <Price />
                        </FieldsetPanel>
                        {isTpf(channel) ? (
                            <FieldsetPanel>
                                <POA />
                            </FieldsetPanel>
                        ) : null}
                    </>
                ),
            }
        } else if (hasSelected(FilterName.price_search_type, 'leasing')) {
            const hasTermsSelected = isSelected(FilterName.term)
            const isBusinessProductType =
                hasSelected(FilterName.lease_product_type, 'business') ||
                (!isSelected(FilterName.lease_product_type) && channel === Channel.vans)
            const priceFilters = isBusinessProductType ? (
                <>
                    <LeaseMinMonthlyPriceExVat />
                    <LeaseMaxMonthlyPriceExVat />
                </>
            ) : (
                <>
                    <LeaseMinMonthlyPrice />
                    <LeaseMaxMonthlyPrice />
                </>
            )
            const Expander = isBusinessProductType ? LeasingPriceExVatExpander : LeasingPriceExpander
            return {
                title: FacetGroupNamesFallback.lease_price_and_terms,
                group: getFacetGroup(FacetGroupName.lease_price_and_terms, queryFacetGroups),
                icon: <IconTag title="Price icon" />,
                children: (
                    <FieldsetPanel>
                        <Expander>
                            <FieldsetPanel>{priceFilters}</FieldsetPanel>
                            {hasTermsSelected ? (
                                <FieldsetPanel singularColumn>
                                    <LeasingDeposit />
                                    <LeasingTerm />
                                    <LeasingYearlyMileage />
                                    <ResetToLowestMonthlyPrice />
                                </FieldsetPanel>
                            ) : (
                                <>
                                    <DefaultTermsText />
                                    <EditTerms />
                                </>
                            )}
                        </Expander>
                    </FieldsetPanel>
                ),
            }
        }

        return {
            title: FacetGroupNamesFallback.price,
            group: getFacetGroup(FacetGroupName.monthly_price, queryFacetGroups),
            icon: <IconTag title="Price icon" />,
            children: (
                <>
                    <NewPriceToggle defaultFinanceValues={getFinanceDefaults(channel)} />
                    {showFlexibleFinance ? (
                        <MonthlyPriceExpander>
                            <FieldsetPanel>
                                <FlexibleMonthlyPriceMin />
                                <FlexibleMonthlyPriceMax />
                            </FieldsetPanel>
                            <FieldsetPanel singularColumn>
                                <Subtitle as="h2">Customise your finance terms</Subtitle>
                                <FlexibleFinanceDeposit />
                                <FlexibleFinanceTerm />
                                <FlexibleFinanceYearlyMileage />
                                <FlexibleFinanceFilterInfoBanner />
                            </FieldsetPanel>
                        </MonthlyPriceExpander>
                    ) : (
                        <MonthlyPriceExpander>
                            <FieldsetPanel>
                                <MonthlyPriceMin />
                                <MonthlyPriceMax />
                            </FieldsetPanel>
                            <FieldsetPanel singularColumn>
                                <FinanceDeposit />
                                <FinanceTerm />
                                <FinanceYearlyMileage />
                                <FinanceDisclaimerText />
                            </FieldsetPanel>
                        </MonthlyPriceExpander>
                    )}
                </>
            ),
        }
    }

    return {
        sort: {
            icon: <IconSort title="Sort filter" />,
            group: {
                status: totalResults === 0 ? FacetGroupStatus.EMPTY : FacetGroupStatus.ENABLED,
                selectedValuesSummary: sortByDisplayText,
                title: 'Sort',
                expandableGroupName: SortGroupName.sort,
            },
            children: <SortOrderRadioButtons />,
        },
        distance: {
            title: FacetGroupNamesFallback.distance,
            group: getFacetGroup(FacetGroupName.distance, queryFacetGroups),
            icon: <IconDistance title="Distance icon" />,
            children: (
                <>
                    <Postcode disabled={isSelected(FilterName.location)} />
                    <Distance disabled={!(isSelected(FilterName.postcode) || isSelected(FilterName.lat_long))} />
                    {isTpf(channel) ? <Location /> : null}
                </>
            ),
        },
        category_tag: {
            title: FacetGroupNamesFallback.category_tag,
            group: getFacetGroup(FacetGroupName.category_tag, queryFacetGroups),
            icon: <IconChannelCar title="New categories" />,
            children: <CategoryTag />,
            badge: <Badge text="New" category={BadgeCategories.tags} />,
        },
        make_and_model: {
            title: FacetGroupNamesFallback.make_and_model,
            group: getFacetGroup(FacetGroupName.make_and_model, queryFacetGroups),
            icon: <IconBrand title="Make icon" />,
            children: (
                <>
                    <Make onValueChanged={onMakeValueChanged} />
                    <Model disabled={!isSelected(FilterName.make)} onValueChanged={onModelValueChanged} />
                    {enableModelVariant && <ModelVariant disabled={!isSelected(FilterName.model)} />}
                </>
            ),
        },
        digital_retailing: {
            title: FacetGroupNamesFallback.with_digital_retailing,
            group: getFacetGroup(FacetGroupName.digital_retailing, queryFacetGroups),
            icon: <IconBuyOnline title={'Reserve Online Icon'} />,
            children: <DigitalRetailingCheckbox />,
            alwaysOpen: true,
        },
        price: getPriceFilters(),
        year: {
            title: FacetGroupNamesFallback.year,
            group: getFacetGroup(FacetGroupName.year, queryFacetGroups),
            icon: <IconCalendar title="Year filter" />,
            singularColumn: false,
            children: <YearMinAndMax />,
        },
        mileage: {
            title: FacetGroupNamesFallback.mileage,
            group: getFacetGroup(FacetGroupName.mileage, queryFacetGroups),
            icon: <IconSpeedometer title="Mileage filter" />,
            singularColumn: false,
            children: <MileageMinAndMax />,
        },
        mileage_km: {
            title: FacetGroupNamesFallback.mileage,
            group: getFacetGroup(FacetGroupName.mileage_km, queryFacetGroups),
            icon: <IconSpeedometer title="Mileage filter" />,
            singularColumn: false,
            children: (
                <>
                    <MileageKmMinFacetAndFilter title="From" />
                    <MileageKmMaxFacetAndFilter title="To" />
                </>
            ),
        },
        gearbox: {
            title: FacetGroupNamesFallback.gearbox,
            group: getFacetGroup(FacetGroupName.gearbox, queryFacetGroups),
            icon: <IconManualGearbox title="Transmission filter" />,
            children: <NewTransmission />,
        },
        body_type: {
            title: FacetGroupNamesFallback.body_type,
            group: getFacetGroup(FacetGroupName.body_type, queryFacetGroups),
            icon: <BodyTypeIcon />,
            children: <BodyTypeCars />,
        },
        colour: {
            title: FacetGroupNamesFallback.colour,
            group: getFacetGroup(FacetGroupName.colour, queryFacetGroups),
            icon: <IconColour title="Colour filter" />,
            children: <ColourCheckboxes />,
        },
        doors: {
            title: FacetGroupNamesFallback.doors,
            group: getFacetGroup(FacetGroupName.doors, queryFacetGroups),
            icon: <IconDoors title="Doors filter" />,
            children: <Doors />,
        },
        seats: {
            title: FacetGroupNamesFallback.seats,
            group: getFacetGroup(FacetGroupName.seats, queryFacetGroups),
            icon: <IconSeats title="Seats filter" />,
            children: <Seats />,
        },
        fuel_type: {
            title: FacetGroupNamesFallback.fuel_type,
            group: getFacetGroup(FacetGroupName.fuel_type, queryFacetGroups),
            icon: <IconFuelType title="Fuel type filter" />,
            children: <FuelType />,
        },
        battery_range: {
            title: FacetGroupNamesFallback.battery_range,
            group: getFacetGroup(FacetGroupName.battery_range, queryFacetGroups),
            icon: <IconRange title="Battery range filter" />,
            children: <BatteryRange />,
        },
        charging_time: {
            title: FacetGroupNamesFallback.charging_time,
            icon: <IconElectric title="Charging time filter" />,
            group: getFacetGroup(FacetGroupName.charging_time, queryFacetGroups),
            children: (
                <>
                    <BatteryChargeTime />
                    <InfoBanner
                        disabled={isChargeDisabled}
                        paragraphs={[
                            'This is how long the car will take to fully charge using a home charging point (7.4kW).',
                        ]}
                    />
                    <BatteryQuickChargeTime />
                    <InfoBanner
                        disabled={isChargeDisabled}
                        paragraphs={[
                            'This is how long the car will take to charge to 80% using a 50kW public charge point.',
                        ]}
                    />
                </>
            ),
        },
        engine_size: {
            title: FacetGroupNamesFallback.engine_size,
            group: getFacetGroup(FacetGroupName.engine_size, queryFacetGroups),
            icon: <IconEngineSize title="Engine size filter" />,
            singularColumn: false,
            children: (
                <>
                    <EngineSizeMin title={ENewFilterRangeDesktop.MIN} />
                    <EngineSizeMax title={ENewFilterRangeDesktop.MAX} />
                </>
            ),
        },
        engine_size_cc: {
            title: FacetGroupNamesFallback.engine_size,
            group: getFacetGroup(FacetGroupName.engine_size_cc, queryFacetGroups),
            icon: <IconEngineSize title="Engine size filter" />,
            singularColumn: false,
            children: (
                <>
                    <EngineSizeCCMin title={ENewFilterRangeDesktop.MIN} />
                    <EngineSizeCCMax title={ENewFilterRangeDesktop.MAX} />
                </>
            ),
        },
        engine_power: {
            title: FacetGroupNamesFallback.engine_power,
            group: getFacetGroup(FacetGroupName.engine_power, queryFacetGroups),
            icon: <IconEnginePower title="Engine power filter" />,
            singularColumn: false,
            children: (
                <>
                    <EnginePowerMin title={ENewFilterRangeDesktop.MIN} />
                    <EnginePowerMax title={ENewFilterRangeDesktop.MAX} />
                </>
            ),
        },
        acceleration: {
            title: FacetGroupNamesFallback.acceleration,
            group: getFacetGroup(FacetGroupName.acceleration, queryFacetGroups),
            icon: <IconAcceleration title="Acceleration filter" />,
            children: <Acceleration />,
        },
        fuel_consumption: {
            title: FacetGroupNamesFallback.fuel_consumption,
            group: getFacetGroup(FacetGroupName.fuel_consumption, queryFacetGroups),
            icon: <IconFuelConsumption title="Fuel consumption filter" />,
            children: <FuelConsumption />,
        },
        co2_emissions: {
            title: FacetGroupNamesFallback.co2_emissions,
            group: getFacetGroup(FacetGroupName.co2_emissions, queryFacetGroups),
            icon: <IconEmissions title="CO2 emissions filter" />,
            children: <Co2Emissions />,
        },
        emission_class: {
            title: FacetGroupNamesFallback.emission_class,
            group: getFacetGroup(FacetGroupName.emission_class, queryFacetGroups),
            children: <EmissionClass />,
        },
        tax_per_year: {
            title: FacetGroupNamesFallback.tax_per_year,
            group: getFacetGroup(FacetGroupName.tax_per_year, queryFacetGroups),
            icon: <IconRoadTax title="Tax filter" />,
            children: <AnnualTax />,
        },
        insurance_group: {
            title: FacetGroupNamesFallback.insurance_group,
            group: getFacetGroup(FacetGroupName.insurance_group, queryFacetGroups),
            icon: <IconInsurance title="Insurance group filter" />,
            children: <InsuranceGroup />,
        },
        drive_type: {
            title: FacetGroupNamesFallback.drive_type,
            group: getFacetGroup(FacetGroupName.drive_type, queryFacetGroups),
            icon: <IconWheel title="Drive type filter" />,
            children: <Drivetrain />,
        },
        boot_space: {
            title: FacetGroupNamesFallback.boot_space,
            group: getFacetGroup(FacetGroupName.boot_space, queryFacetGroups),
            icon: <IconBootSpace title="Boot space filter" />,
            children: <BootSpace />,
        },
        seller_type: {
            title: FacetGroupNamesFallback.seller_type,
            group: getFacetGroup(FacetGroupName.seller_type, queryFacetGroups),
            icon: <IconSellerType title="Seller type filter" />,
            children: <SellerType />,
        },
        previously_written_off: {
            title: FacetGroupNamesFallback.previously_written_off,
            group: getFacetGroup(FacetGroupName.previously_written_off, queryFacetGroups),
            icon: <IconWrittenOff title="Category filter" />,
            children: <WriteOff />,
        },
        keyword_search: {
            title: FacetGroupNamesFallback.keyword_search,
            group: getFacetGroup(FacetGroupName.keyword_search, queryFacetGroups),
            icon: <IconSearch title="Keywords filter" />,
            children: <Keywords placeholder={getKeywordsPlaceholder(channel)} />,
            alwaysOpen: true,
        },
        frame_size_cm: {
            title: FacetGroupNamesFallback.frame_size_cm,
            group: getFacetGroup(FacetGroupName.frame_size, queryFacetGroups),
            icon: <IconBikeframe title="Frame Size Filter" />,
            singularColumn: false,
            children: (
                <>
                    <FrameSizeMinFacetAndFilter title={ENewFilterRangeDesktop.MIN} />
                    <FrameSizeMaxFacetAndFilter title={ENewFilterRangeDesktop.MAX} />
                </>
            ),
        },
        style: {
            title: FacetGroupNamesFallback.style,
            group: getFacetGroup(FacetGroupName.style, queryFacetGroups),
            icon: channel === Channel.cycles ? <IconEbike title="Category filter" /> : null,
            children: !isTpf(channel) ? (
                <CategoryNoLabel />
            ) : (
                <>
                    <Category onValueChanged={onStyleValueChanged} />
                    <SubCategory disabled={!isSelected(FilterName.style)} />
                </>
            ),
        },
        wheelbase: {
            title: FacetGroupNamesFallback.wheelbase,
            group: getFacetGroup(FacetGroupName.wheelbase, queryFacetGroups),
            icon: <IconDrivetrain title="Wheelbase" />,
            children: <WheelBase />,
        },
        cab_type: {
            title: FacetGroupNamesFallback.cab_type,
            group: getFacetGroup(FacetGroupName.cab_type, queryFacetGroups),
            icon: <IconCabType title="Cab Type" />,
            children: <CabType />,
        },
        vat_status: {
            title: FacetGroupNamesFallback.vat_status,
            group: getFacetGroup(FacetGroupName.vat_status, queryFacetGroups),
            icon: <IconVAT title="VAT Status" />,
            children: <VATStatus />,
        },
        axle_config: {
            title: FacetGroupNamesFallback.axle_config,
            group: getFacetGroup(FacetGroupName.axle_config, queryFacetGroups),
            children: <AxleConfig />,
        },
        driver_position: {
            title: FacetGroupNamesFallback.driver_position,
            group: getFacetGroup(FacetGroupName.driver_position, queryFacetGroups),
            children: <DriverPosition />,
        },
        gross_vehicle_weight: {
            title: FacetGroupNamesFallback.gross_vehicle_weight,
            group: getFacetGroup(FacetGroupName.gross_vehicle_weight, queryFacetGroups),
            singularColumn: false,
            children: (
                <>
                    <GrossVehicleWeightMin title={ENewFilterRangeDesktop.MIN} />
                    <GrossVehicleWeightMax title={ENewFilterRangeDesktop.MAX} />
                </>
            ),
        },
        berth: {
            title: FacetGroupNamesFallback.berth,
            group: getFacetGroup(FacetGroupName.berth, queryFacetGroups),
            children: <Berth title="Berth filter" />,
        },
        trailer_axle_number: {
            title: FacetGroupNamesFallback.trailer_axle_number,
            group: getFacetGroup(FacetGroupName.trailer_axle_number, queryFacetGroups),
            children: <Axles title="Axles filter" />,
        },
        bedroom_layout: {
            title: FacetGroupNamesFallback.bedroom_layout,
            group: getFacetGroup(FacetGroupName.bedroom_layout, queryFacetGroups),
            children: <BedroomLayout title="Bedroom layout filter" />,
        },
        end_layout: {
            title: FacetGroupNamesFallback.end_layout,
            group: getFacetGroup(FacetGroupName.end_layout, queryFacetGroups),
            children: <EndLayout title="End layout filter" />,
        },
        mtplm: {
            title: FacetGroupNamesFallback.mtplm,
            group: getFacetGroup(FacetGroupName.mtplm, queryFacetGroups),
            singularColumn: false,
            children: (
                <>
                    <MTPLMMinFacetAndFilter title={ENewFilterRangeDesktop.MIN} />
                    <MTPLMMaxFacetAndFilter title={ENewFilterRangeDesktop.MAX} />
                </>
            ),
        },
        exterior_length: {
            title: FacetGroupNamesFallback.exterior_length,
            group: getFacetGroup(FacetGroupName.exterior_length, queryFacetGroups),
            singularColumn: false,
            children: (
                <>
                    <LengthMinFacetAndFilter title={ENewFilterRangeDesktop.MIN} />
                    <LengthMaxFacetAndFilter title={ENewFilterRangeDesktop.MAX} />
                </>
            ),
        },
        seats_range: {
            title: FacetGroupNamesFallback.seats,
            group: getFacetGroup(FacetGroupName.seats_range, queryFacetGroups),
            singularColumn: false,
            children: (
                <>
                    <SeatsMinFacetAndFilter title={ENewFilterRangeDesktop.MIN} />
                    <SeatsMaxFacetAndFilter title={ENewFilterRangeDesktop.MAX} />
                </>
            ),
        },
        gross_trailer_weight: {
            title: FacetGroupNamesFallback.gross_trailer_weight,
            group: getFacetGroup(FacetGroupName.gross_trailer_weight, queryFacetGroups),
            singularColumn: false,
            children: (
                <>
                    <GrossTrailerWeightMin title={ENewFilterRangeDesktop.MIN} />
                    <GrossTrailerWeightMax title={ENewFilterRangeDesktop.MAX} />
                </>
            ),
        },
        hours_used: {
            title: FacetGroupNamesFallback.hours_used,
            group: getFacetGroup(FacetGroupName.hours_used, queryFacetGroups),
            singularColumn: false,
            children: (
                <>
                    <HoursUsedMin title={ENewFilterRangeDesktop.MIN} />
                    <HoursUsedMax title={ENewFilterRangeDesktop.MAX} />
                </>
            ),
        },
        manufacturer_approved: {
            title: FilterName.is_manufacturer_approved,
            children: <ManufacturerApproved />,
        },
        include_ni_only: {
            title: FilterName.ni_only,
            children: <NIOnly />,
        },
        lease_in_stock: {
            title: 'In stock and available now',
            icon: <IconCalendar title="In stock and available now" />,
            children: <LeaseInStockToggle />,
        },
        lease_product_type: {
            title: '',
            icon: null,
            children: <LeaseProductTypeToggle />,
        },
    }
}
